import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const LogoContainer = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.5rem;
  }
`;

const LogoIcon = styled.div<{ isB2B?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.isB2B ? '#ffffff' : '#1a1a2e'};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: ${props => props.isB2B ? '0 2px 4px rgba(0,0,0,0.1)' : 'none'};

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const PublicLogoSvg = styled.svg`
  width: 40px;
  height: 40px;

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

const B2BLogoSvg = styled.svg`
  width: 40px;
  height: 40px;

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
  }
`;

const LogoText = styled.div<{ isB2B?: boolean }>`
  font-size: ${props => props.isB2B ? '1.75rem' : '1.5rem'};
  font-weight: 800;
  color: ${props => props.isB2B ? '#1a1a2e' : '#1a1a2e'};
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  letter-spacing: -0.02em;

  @media (max-width: 480px) {
    font-size: ${props => props.isB2B ? '1.25rem' : '1rem'};
  }
`;

const SubText = styled.div<{ isB2B?: boolean }>`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  background: ${props => props.isB2B ? 'transparent' : 'linear-gradient(90deg, #ff6b6b 0%, #4ecdc4 100%)'};
  -webkit-background-clip: ${props => props.isB2B ? 'none' : 'text'};
  -webkit-text-fill-color: ${props => props.isB2B ? '#666' : 'transparent'};
  padding: 2px 0;

  @media (max-width: 480px) {
    font-size: 0.6rem;
    letter-spacing: 0.15em;
  }
`;

export const Logo: React.FC = () => {
  const location = useLocation();
  const isB2B = location.pathname === '/business';

  return (
    <LogoContainer to={isB2B ? '/business' : '/'}>
      <LogoIcon isB2B={isB2B}>
        {isB2B ? (
          <B2BLogoSvg viewBox="0 0 40 40">
            {/* SpinToConnect B2B Logo - Network style */}
            <circle cx="20" cy="20" r="19" fill="none" stroke="#000" strokeWidth="1.5"/>
            <circle cx="20" cy="20" r="6" fill="#000"/>
            <g transform="translate(20 20)">
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
                <g key={i} transform={`rotate(${angle})`}>
                  <line 
                    x1="0" 
                    y1="6" 
                    x2="0" 
                    y2="13" 
                    stroke="#000" 
                    strokeWidth="1.5"
                  />
                  <circle 
                    cx="0" 
                    cy="15" 
                    r="2.5" 
                    fill={i % 2 === 0 ? '#ff6b6b' : '#4ecdc4'}
                  />
                </g>
              ))}
            </g>
          </B2BLogoSvg>
        ) : (
          <PublicLogoSvg viewBox="0 0 40 40">
            {/* Wheel of Matches Public Logo - Colorful wheel */}
            <defs>
              <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#ff6b6b', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: '#ffd93d', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: '#4ecdc4', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <circle cx="20" cy="20" r="19" fill="none" stroke="white" strokeWidth="2"/>
            <g transform="translate(20 20)">
              {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map((angle, i) => (
                <path
                  key={i}
                  d={`M 0 0 L 17 0 A 17 17 0 0 0 ${17 * Math.cos((angle + 30) * Math.PI / 180)} ${17 * Math.sin((angle + 30) * Math.PI / 180)} Z`}
                  transform={`rotate(${angle})`}
                  fill={`hsl(${angle}, 80%, 65%)`}
                  opacity="0.9"
                />
              ))}
            </g>
            <circle cx="20" cy="20" r="4" fill="white"/>
          </PublicLogoSvg>
        )}
      </LogoIcon>
      <LogoText isB2B={isB2B}>
        {isB2B ? (
          <>
            SpinToConnect
            <SubText isB2B={isB2B}>BUSINESS</SubText>
          </>
        ) : (
          <>
            SpinToConnect
            <SubText isB2B={isB2B}></SubText>
          </>
        )}
      </LogoText>
    </LogoContainer>
  );
}; 